import React from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';

import Conditional from '../common/conditional';
import DocumentNumberPreview from '../themed/document-number-preview';
import Progress from '../themed/progress';
import Form from '../common/form';

import * as authService from '../../services/authService';
import * as divisionService from '../../services/divisionService';
import * as projectService from '../../services/projectService';
import * as toastService from '../../services/toastService';

import * as utils from '../../utils/utilities';
import * as models from '../../utils/models';
import * as ids from '../../utils/ids'

import { getProjectTitleOptions } from '../../utils/data';

import withInspectionTypes from '../hoc/withInspectionTypes';
import withOrganizations from '../hoc/withOrganizations';
import withProjects from '../hoc/withProjects';
import withUsers from '../hoc/withUsers';

import config from '../../utils/config';

interface FormData {
	projectCreationYear: string;
	projectTitle: string;
	equipmentDescription: string;
	inspectionTypeId: string;
	organisationId: string;
	divisionId: string;
	contactPersonId?: string;
	documentNumberPrefix: string;
	supervisorId: string;
	facilityId: string;
	scope: string;
	location: string;
	module: string;
	equipmentTag: string;
}

interface FormProps {
	types: models.SelectOption[];
}

interface FormState {
	data: FormData;
	errors: object;
	loading: boolean;
	progress: number;
	step: number;
	division: models.Division | object;
}

class ImportProject extends Form {
	state = {
		data: initialData,
		errors: {},
		loading: false,
		progress: 0,
		step: 1,
		division: {},
	};

	schema = {
		projectCreationYear: Joi.number().positive().min(1990).max(new Date().getFullYear()).required().label('Project Creation Year'),
		projectTitle: Joi.string().min(5).required().label('Title'),
		scope: Joi.string().min(3).max(50).required().label('Scope'),
		inspectionTypeId: Joi.string().required().label('Inspection Type'),
		supervisorId: Joi.string().required().label('Inspector'),
		contactPersonId: Joi.string().optional().label('Contact Person'),
		equipmentTag: Joi.string().min(4).required().label('Equipment Tag No'),
		facilityId: Joi.string().required().label('Facility'),
		organisationId: Joi.string().required().label('Organization ID'),
		divisionId: Joi.string().required().label('Division ID'),
		documentNumberPrefix: Joi.string().when('divisionId', {
			is: ids.TOPSIDES_DIVISION_ID,
			then: Joi.string().max(this.props.project ? 255 : 50).required().label('Document Number Prefix'),
			otherwise: Joi.string().allow('').label('Document Number Prefix'),
		}),
		equipmentDescription: Joi.string().min(10).required().label('Description'),
		location: Joi.string().min(5).required().label('Location'),
		module: Joi.string().min(1).required().label('Module'),
	};

	static propTypes = {
		onCreateProject: PropTypes.func,
	}

	componentDidUpdate(prevProps: Readonly<FormProps>, prevState: Readonly<FormState>, snapshot?: any): void {
		if (prevState.data.divisionId !== this.state.data.divisionId) {
			this.getDivisionDetails();
		}
	}

	getDocumentNumberPrefix = () => {
		return this.state.data.documentNumberPrefix.toUpperCase().split(' ').join('-');
	}

	getApiData = () => {
		const apiData: any = { ...this.state.data };
		apiData.documentNumberPrefix = this.getDocumentNumberPrefix();

		return apiData;
	};

	getSelectedOrganization = (): models.Organization | undefined => {
		const allOrganizations: models.Organization[] = this.props.organizations;
		return allOrganizations.find((o) => o._id === this.state.data.organisationId);
	};

	getSelectedInspectionType = () => {
		return this.props.types.find((t: models.SelectOption) => t.id === this.state.data.inspectionTypeId);
	};

	getDivisionDetails = async (): Promise<void> => {
		this.setState({ loading: true });

		try {
			const { divisionId } = this.state.data;
			const { data } = await divisionService.getOneDivision(divisionId);

			this.setState({ loading: false, division: data.division } as any);
		} catch (ex) {
			this.setState({ loading: false });
		}
	};

	getFacilitiesByOrganization = (): models.SelectOption[] => {
		const organization = this.getSelectedOrganization();
		if (!organization) return [];

		return organization.facilities.map(utils.transformFacilityItem);
	};

	getInspectionTypes = (): models.SelectOption[] => {
		const { inspectionType } = this.state.division as models.Division;

		if (inspectionType) {
			return inspectionType.map(utils.transformInspectionType);
		}

		return [];
	};

	getUsersInOrganization = (users: models.User[]) => {
		return (
			users
			.filter((user) => user.organisationId === this.state.data.organisationId)
			.map(utils.transformUserItem)
		);
	};

	getOrganizationContactPersons = (): models.SelectOption[] => {
		const organization = this.getSelectedOrganization();
		if (organization) return this.getUsersInOrganization(organization.contactPersons);
		
		return [];
	};

	getSupervisors = (): models.SelectOption[] => {
		const users: models.User[] = (this.state.division as any).users ?? [];
		const supervisors: models.SelectOption[] = this.props.supervisors;

		const userIds = users.map((user) => user.id);
		return supervisors.filter((s) => userIds.includes(s.id));
	};

	getUserDivisions = (): models.SelectOption[] => {
		const user = authService.getCurrentUser() as models.User;
		return user.division.map((id: string, index: number) => ({
			id,
			name: user.divisionName[index],
		}));
	};

	postData = async (): Promise<void> => {
		const response = await projectService.importProject(this.getApiData(), this.handleProgressChange);
		this.props.onCreateProject(response.data);
	};

	doSubmit = (): void => {
		toastService.promise(this.postData(), {
			loading: `Importing Project`,
			success: () => `Project Imported`,
			error: utils.getErrorMessage,
		});
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form
					autoComplete="off"
					onSubmit={this.handleSubmit}
					className="p-3 container"
				>
						<h1 className="form-title">Import Project</h1>

						<p className="form-subtitle">
							Complete the details below to import a project
						</p>

						<div className="row">
							<div className="col">
								{this.renderInput({
									type: 'number',
									name: 'projectCreationYear',
									label: 'Project Creation Year',
									placeholder: 'E.g. ' + new Date().getFullYear(),
								})}
							</div>

							<div className="col">
								{this.renderSelect({
									name: 'divisionId',
									label: 'Division',
									placeholder: 'Select Division',
									options: this.getUserDivisions(),
								})}
							</div>

							<div className="col">
								{this.renderSelect({
									name: 'projectTitle',
									label: 'Project Title',
									placeholder: 'Select Inspection',
									options: getProjectTitleOptions(),
								})}
							</div>
							
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'scope',
									label: 'Project Scope',
									placeholder: 'Enter Project Scope',
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderSelect({
									name: 'organisationId',
									label: 'Organization',
									placeholder: 'Select Organization',
									options: this.props.organizationsForDropdown,
								})}
							</div>

							<div className="col">
								{this.renderSelect({
									name: 'facilityId',
									label: 'Facility',
									placeholder: 'E.g. USAN FPSO',
									options: this.getFacilitiesByOrganization(),
								})}
							</div>

							<Conditional isVisible={config.enableContactPerson}>
								<div className="col">
									{this.renderSelect({
										name: 'contactPersonId',
										label: 'Contact Person',
										placeholder: 'Select Contact Person',
										options: this.getOrganizationContactPersons(),
									})}
								</div>
							</Conditional>

							<div className="col">
								{this.renderSelect({
									name: 'inspectionTypeId',
									label: 'Inspection Type',
									placeholder: 'Select Inspection Type',
									options: this.getInspectionTypes(),
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'equipmentTag',
									label: 'Equipment Tag',
									placeholder: 'E.g. AGH-61637',
								})}
							</div>

							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'location',
									label: 'Location',
									placeholder: 'E.g. Agbara Platform',
								})}
							</div>

							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'module',
									label: 'Module',
									placeholder: 'E.g. G MAIN DECK',
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						{this.renderTextArea({
							name: 'equipmentDescription',
							label: 'Description',
							placeholder: 'E.g. Agbara Helideck',
							rows: 3,
						})}

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'documentNumberPrefix',
									label: 'Document Number Prefix',
									placeholder: 'Enter Document Number Prefix',
								})}

								<DocumentNumberPreview documentNumber={`XXXX/XXX/XXX/XXX/XXX/${this.getDocumentNumberPrefix()}/XXX`} />
							</div>
							<div className="col">
								{this.renderSelect({
									name: 'supervisorId',
									label: 'Assign To Inspector',
									placeholder: 'Select Inspector',
									options: this.getSupervisors(),
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						<div className="d-flex justify-content-end">
							{this.renderButton(this.state.loading ? 'Uploading' : 'Submit')}
						</div>
				</form>
			</React.Fragment>
		);
	}
}

const initialData: FormData = {
	projectCreationYear: '',
	projectTitle: '',
	equipmentDescription: '',
	inspectionTypeId: '',
	documentNumberPrefix: '',
	organisationId: '',
	divisionId: '',
	supervisorId: '',
	facilityId: '',
	location: '',
	module: '',
	scope: '',
	equipmentTag: '',
};

const Projects = withProjects(ImportProject);
export default withUsers(withOrganizations(withInspectionTypes(Projects)));
